/**
 * Sélecteur de devise intelligent avec affichage des soldes
 * Permet de basculer entre les devises disponibles
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import { formatPrice } from '../../../config/currencies';

const CurrencySelector = ({ 
  currencies = [], 
  selectedCurrency, 
  onCurrencyChange,
  variant = "outlined",
  size = "medium",
  showBalances = true 
}) => {
  const { t } = useTranslation('financial');
  
  const handleChange = (event) => {
    onCurrencyChange(event.target.value);
  };

  if (currencies.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('currencySelector.noCurrencies', 'Aucune devise disponible')}
      </Typography>
    );
  }

  return (
    <FormControl fullWidth variant={variant} size={size}>
      <InputLabel id="currency-selector-label">
        {t('currencySelector.activeCurrency', 'Devise Active')}
      </InputLabel>
      <Select
        labelId="currency-selector-label"
        value={selectedCurrency || ''}
        label={t('currencySelector.activeCurrency', 'Devise Active')}
        onChange={handleChange}
        renderValue={(selected) => {
          const selectedCurrencyInfo = currencies.find(c => c.currency === selected);
          if (!selectedCurrencyInfo) return selected;
          
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar 
                sx={{ 
                  width: 24, 
                  height: 24, 
                  bgcolor: 'primary.main',
                  fontSize: '0.75rem'
                }}
              >
                {selected}
              </Avatar>
              <Box>
                <Typography component="span" fontWeight="medium">
                  {selected}
                </Typography>
                {showBalances && (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({formatPrice(selectedCurrencyInfo.balance, selected)})
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      >
        {currencies.map((currencyInfo) => (
          <MenuItem key={currencyInfo.currency} value={currencyInfo.currency}>
            <ListItemAvatar>
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: currencyInfo.balance > 0 ? 'success.main' : 'grey.400',
                  fontSize: '0.875rem'
                }}
              >
                {currencyInfo.currency}
              </Avatar>
            </ListItemAvatar>
            <ListItemText>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {currencyInfo.currency}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('currencySelector.transactionCount', { count: currencyInfo.transaction_count, defaultValue: `${currencyInfo.transaction_count} transaction${currencyInfo.transaction_count > 1 ? 's' : ''}` })}
                  </Typography>
                  {showBalances && (
                    <Typography 
                      variant="body2" 
                      color={currencyInfo.balance > 0 ? 'success.main' : 'text.secondary'}
                      fontWeight="medium"
                    >
                      {formatPrice(currencyInfo.balance, currencyInfo.currency)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </ListItemText>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CurrencySelector;
